import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useApolloClients } from "~/components/ApolloClients";
import {
  GET_INTERVIEW_GUIDES,
  GET_INTERVIEW_GUIDE,
  CREATE_INTERVIEW_GUIDE,
  GENERATE_AI_GUIDE,
  GENERATE_GUIDE_QUESTIONS,
  GENERATE_GUIDE_QUESTIONS_PREVIEW,
  REFINE_GUIDE_QUESTION,
  GENERATE_DRAFT_QUESTION,
  UPDATE_INTERVIEW_GUIDE,
  SAVE_INTERVIEW_GUIDE,
  ARCHIVE_INTERVIEW_GUIDE,
  RESTORE_INTERVIEW_GUIDE,
  DELETE_INTERVIEW_GUIDE,
  DUPLICATE_INTERVIEW_GUIDE,
  ADD_GUIDE_QUESTION,
  UPDATE_GUIDE_QUESTION,
  REMOVE_GUIDE_QUESTION,
  REORDER_GUIDE_QUESTIONS,
  LOCK_GUIDE_QUESTION,
  SAVE_QUESTION_TO_BANK,
  REQUEST_INPUT,
  REMOVE_INPUT_REQUEST,
} from "./operations";
import type {
  InterviewGuideFilter,
  InterviewGuideDisplay,
  InterviewGuideListItem,
  InterviewGuideCreateInput,
  AIGuideGenerationInput,
  AIQuestionsGenerationInput,
  InterviewGuideUpdateInput,
  InterviewGuideSaveInput,
  GeneratedGuideQuestion,
  GuideQuestionInput,
  GuideQuestionUpdateInput,
  SaveToBankInput,
  ResponseFormat,
  ScoringGuide,
  GuideGenerationStatus,
} from "../types";
import { toGuideDisplay, toGuideListItem } from "../mappers/guideMapper";
import { executeMutation } from "./mutationHelpers";

export interface GuideListPageInfo {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export function useInterviewGuides(
  filter: InterviewGuideFilter,
  /**
   * Server-side pagination. When omitted, fetch a large single page so
   * non-paginated consumers (e.g. the interview-detail template picker) see
   * the whole collection — the backend otherwise defaults to 25 items.
   */
  pagination?: { page: number; pageSize: number },
) {
  const { federationGatewayClient } = useApolloClients();

  const { data, previousData, loading, error, refetch } = useQuery(
    GET_INTERVIEW_GUIDES,
    {
      client: federationGatewayClient,
      variables: {
        filter: {
          status: filter.status,
          archived: filter.archived,
          search: filter.search,
          division: filter.division,
          jobTitle: filter.jobTitle,
        },
        pagination: pagination ?? { page: 1, pageSize: 500 },
      },
      fetchPolicy: "cache-and-network",
    },
  );

  // Changing page/filter makes `data` undefined while the new variables load;
  // fall back to the previous result so guides/pageInfo don't collapse to an
  // empty single page mid-transition (which would bounce the pager back).
  const effective = data ?? previousData;

  const guides: InterviewGuideListItem[] = useMemo(
    () => (effective?.interviewGuides?.items || []).map(toGuideListItem),
    [effective?.interviewGuides?.items],
  );

  const pageInfo: GuideListPageInfo = effective?.interviewGuides?.pageInfo ?? {
    page: pagination?.page ?? 1,
    pageSize: pagination?.pageSize ?? 500,
    totalItems: guides.length,
    totalPages: 1,
  };

  return { guides, pageInfo, loading, error, refetch };
}

export function useInterviewGuide(guideId: string | null) {
  const { federationGatewayClient } = useApolloClients();

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(
    GET_INTERVIEW_GUIDE,
    {
      client: federationGatewayClient,
      variables: { id: guideId },
      skip: !guideId,
      fetchPolicy: "cache-and-network",
    },
  );

  const guide: InterviewGuideDisplay | null = useMemo(
    () => (data?.interviewGuide ? toGuideDisplay(data.interviewGuide) : null),
    [data?.interviewGuide],
  );

  // AI guide generation runs in a background worker (calendar publishes the
  // request to gen-ai over EventBridge), so the editor opens on a shell guide
  // with generationStatus GENERATING. Poll until gen-ai's completion flips it to
  // READY / FAILED, then stop.
  const isGenerating = guide?.generationStatus === "GENERATING";
  useEffect(() => {
    if (isGenerating) {
      startPolling(4000);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [isGenerating, startPolling, stopPolling]);

  return { guide, loading, error, refetch };
}

export function useInterviewGuideMutations() {
  const { federationGatewayClient } = useApolloClients();

  // Mutations that change the *set* of guides (or their list-visible fields)
  // need to refresh the active `GetInterviewGuides` query. Refetching by name
  // re-uses the current variables (including the active filter) so the
  // currently displayed filtered list stays consistent. Per-entity mutations
  // rely on Apollo's normalised cache (entities have stable `id`s).
  const refetchList = {
    refetchQueries: ["GetInterviewGuides"],
    awaitRefetchQueries: false,
  };

  const [createGuide] = useMutation(CREATE_INTERVIEW_GUIDE, {
    client: federationGatewayClient,
    ...refetchList,
  });
  const [generateAIGuide] = useMutation(GENERATE_AI_GUIDE, {
    client: federationGatewayClient,
    ...refetchList,
  });
  const [generateGuideQuestions] = useMutation(GENERATE_GUIDE_QUESTIONS, {
    client: federationGatewayClient,
  });
  const [refineQuestion] = useMutation(REFINE_GUIDE_QUESTION, {
    client: federationGatewayClient,
  });
  const [generateDraftQuestion] = useMutation(GENERATE_DRAFT_QUESTION, {
    client: federationGatewayClient,
  });
  const [updateGuide] = useMutation(UPDATE_INTERVIEW_GUIDE, {
    client: federationGatewayClient,
  });
  // Batch save (Save-and-close / Publish / Move-to-draft). Refetch the list so
  // the row's status (and thus its "available for interview" state) updates.
  const [saveGuide] = useMutation(SAVE_INTERVIEW_GUIDE, {
    client: federationGatewayClient,
    ...refetchList,
  });
  const [archiveGuide] = useMutation(ARCHIVE_INTERVIEW_GUIDE, {
    client: federationGatewayClient,
    ...refetchList,
  });
  const [restoreGuide] = useMutation(RESTORE_INTERVIEW_GUIDE, {
    client: federationGatewayClient,
    ...refetchList,
  });
  const [deleteGuide] = useMutation(DELETE_INTERVIEW_GUIDE, {
    client: federationGatewayClient,
    ...refetchList,
  });
  const [duplicateGuide] = useMutation(DUPLICATE_INTERVIEW_GUIDE, {
    client: federationGatewayClient,
    ...refetchList,
  });
  const [addQuestion] = useMutation(ADD_GUIDE_QUESTION, {
    client: federationGatewayClient,
  });
  const [updateQuestion] = useMutation(UPDATE_GUIDE_QUESTION, {
    client: federationGatewayClient,
  });
  const [removeQuestion] = useMutation(REMOVE_GUIDE_QUESTION, {
    client: federationGatewayClient,
  });
  const [reorderQuestions] = useMutation(REORDER_GUIDE_QUESTIONS, {
    client: federationGatewayClient,
  });
  const [lockQuestion] = useMutation(LOCK_GUIDE_QUESTION, {
    client: federationGatewayClient,
  });
  const [saveToBank] = useMutation(SAVE_QUESTION_TO_BANK, {
    client: federationGatewayClient,
  });
  // Reviewer input is managed independently of the content draft (it persists
  // across publish and is only cleared via the "×"), so request/remove stay
  // immediate mutations. Refetch the list so row avatars update.
  const [requestInput] = useMutation(REQUEST_INPUT, {
    client: federationGatewayClient,
    ...refetchList,
  });
  const [removeInputRequest] = useMutation(REMOVE_INPUT_REQUEST, {
    client: federationGatewayClient,
    ...refetchList,
  });
  // Non-persisting: returns generated questions for the editor to hold locally.
  const [generateQuestionsPreview] = useMutation(
    GENERATE_GUIDE_QUESTIONS_PREVIEW,
    { client: federationGatewayClient },
  );

  // AI generation runs off-gateway (calendar → gen-ai over EventBridge), so
  // `generateAIGuide` only returns a GENERATING shell. Poll the guide until
  // gen-ai's completion flips it to a terminal status — this lets the create
  // wizard keep its build spinner up and open the editor on the finished guide.
  // Never throws: on a read error or timeout it resolves (still GENERATING) so
  // navigation proceeds and the editor's own poll becomes the backstop.
  const waitForGuideGeneration = useCallback(
    async (
      guideId: string,
      {
        intervalMs = 2500,
        timeoutMs = 240000,
      }: {
        intervalMs?: number;
        timeoutMs?: number;
      } = {},
    ): Promise<{
      status: GuideGenerationStatus;
      questionsCount: number;
    }> => {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        try {
          const { data } = await federationGatewayClient.query({
            query: GET_INTERVIEW_GUIDE,
            variables: { id: guideId },
            fetchPolicy: "network-only",
          });
          const guide = data?.interviewGuide;
          const status = (guide?.generationStatus ??
            "NONE") as GuideGenerationStatus;
          if (status !== "GENERATING") {
            return { status, questionsCount: guide?.questionsCount ?? 0 };
          }
        } catch {
          return { status: "GENERATING", questionsCount: 0 };
        }
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
      return { status: "GENERATING", questionsCount: 0 };
    },
    [federationGatewayClient],
  );

  return {
    waitForGuideGeneration,
    createGuide: async (input: InterviewGuideCreateInput) => {
      const result = await executeMutation(
        "createGuide",
        () => createGuide({ variables: { input } }),
        (d: any) => d.interviewGuideCreate,
      );
      return result;
    },
    generateAIGuide: async (input: AIGuideGenerationInput) => {
      const result = await executeMutation(
        "generateAIGuide",
        () => generateAIGuide({ variables: { input } }),
        (d: any) => d.interviewGuideGenerateAI,
      );
      return result;
    },
    generateGuideQuestions: async (
      guideId: string,
      input: AIQuestionsGenerationInput,
    ) => {
      const result = await executeMutation(
        "generateGuideQuestions",
        () => generateGuideQuestions({ variables: { guideId, input } }),
        (d: any) => d.interviewGuideGenerateQuestions,
      );
      return result;
    },
    refineQuestion: async (input: {
      questionText: string;
      modification: string;
      responseFormat?: ResponseFormat;
    }): Promise<{ text: string; scoringGuide?: ScoringGuide } | null> => {
      const { data } = await refineQuestion({ variables: { input } });
      return data?.interviewGuideRefineQuestion ?? null;
    },
    generateDraft: async (
      context: string,
      responseFormat?: ResponseFormat,
    ): Promise<{ text: string; scoringGuide?: ScoringGuide } | null> => {
      const { data } = await generateDraftQuestion({
        variables: { input: { context, responseFormat } },
      });
      return data?.interviewGuideGenerateDraft ?? null;
    },
    updateGuide: async (id: string, input: InterviewGuideUpdateInput) => {
      const result = await executeMutation(
        "updateGuide",
        () => updateGuide({ variables: { id, input } }),
        (d: any) => d.interviewGuideUpdate,
      );
      return result;
    },
    saveGuide: async (
      id: string,
      input: InterviewGuideSaveInput,
      publish: boolean,
    ) => {
      const result = await executeMutation(
        "saveGuide",
        () => saveGuide({ variables: { id, input, publish } }),
        (d: any) => d.interviewGuideSave,
      );
      return result;
    },
    archiveGuide: async (id: string) => {
      const result = await executeMutation(
        "archiveGuide",
        () => archiveGuide({ variables: { id } }),
        (d: any) => d.interviewGuideArchive,
      );
      return result;
    },
    restoreGuide: async (id: string) => {
      const result = await executeMutation(
        "restoreGuide",
        () => restoreGuide({ variables: { id } }),
        (d: any) => d.interviewGuideRestore,
      );
      return result;
    },
    deleteGuide: async (id: string) => {
      const result = await executeMutation(
        "deleteGuide",
        () => deleteGuide({ variables: { id } }),
        (d: any) => d.interviewGuideDelete,
      );
      return result;
    },
    duplicateGuide: async (id: string) => {
      const result = await executeMutation(
        "duplicateGuide",
        () => duplicateGuide({ variables: { id } }),
        (d: any) => d.interviewGuideDuplicate,
      );
      return result;
    },
    addQuestion: async (guideId: string, input: GuideQuestionInput) => {
      const result = await executeMutation(
        "addQuestion",
        () => addQuestion({ variables: { guideId, input } }),
        (d: any) => d.interviewGuideQuestionAdd,
      );
      return result;
    },
    updateQuestion: async (
      guideId: string,
      questionId: string,
      input: GuideQuestionUpdateInput,
    ) => {
      const result = await executeMutation(
        "updateQuestion",
        () => updateQuestion({ variables: { guideId, questionId, input } }),
        (d: any) => d.interviewGuideQuestionUpdate,
      );
      return result;
    },
    removeQuestion: async (guideId: string, questionId: string) => {
      const result = await executeMutation(
        "removeQuestion",
        () => removeQuestion({ variables: { guideId, questionId } }),
        (d: any) => d.interviewGuideQuestionRemove,
      );
      return result;
    },
    reorderQuestions: async (guideId: string, questionIds: string[]) => {
      const result = await executeMutation(
        "reorderQuestions",
        () => reorderQuestions({ variables: { guideId, questionIds } }),
        (d: any) => d.interviewGuideQuestionsReorder,
      );
      return result;
    },
    lockQuestion: async (
      guideId: string,
      questionId: string,
      locked: boolean,
    ) => {
      const result = await executeMutation(
        "lockQuestion",
        () => lockQuestion({ variables: { guideId, questionId, locked } }),
        (d: any) => d.interviewGuideQuestionLock,
      );
      return result;
    },
    saveToBank: async (
      guideId: string,
      questionId: string,
      input?: SaveToBankInput,
    ) => {
      const result = await executeMutation(
        "saveToBank",
        () => saveToBank({ variables: { guideId, questionId, input } }),
        (d: any) => d.interviewGuideQuestionSaveToBank,
      );
      return result;
    },
    requestInput: async (id: string, userIds: string[], message?: string) => {
      const result = await executeMutation(
        "requestInput",
        () => requestInput({ variables: { id, input: { userIds, message } } }),
        (d: any) => d.interviewGuideRequestInput,
      );
      return result;
    },
    removeInputRequest: async (id: string, userId: string) => {
      const result = await executeMutation(
        "removeInputRequest",
        () => removeInputRequest({ variables: { id, userId } }),
        (d: any) => d.interviewGuideRemoveInputRequest,
      );
      return result;
    },
    // Non-persisting preview — returns generated questions (bare list, no
    // MutationResult), so it doesn't go through executeMutation.
    generateQuestionsPreview: async (
      guideId: string,
      input: AIQuestionsGenerationInput,
    ): Promise<GeneratedGuideQuestion[]> => {
      const { data } = await generateQuestionsPreview({
        variables: { guideId, input },
      });
      return data?.interviewGuideGenerateQuestionsPreview ?? [];
    },
  };
}
